import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  CircularProgress,
  AppBar,
  Toolbar,
} from '@mui/material';
import { Visibility, VisibilityOff, Logout as LogoutIcon } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { getToken } from '@/utils/storage';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ninshousystem-api-361153976392.asia-northeast1.run.app';

export default function MyPage() {
  const { user, logout } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changing, setChanging] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      setSnackbar({ open: true, message: '新しいパスワードは8文字以上で入力してください', severity: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'パスワードが一致しません', severity: 'error' });
      return;
    }

    setChanging(true);
    try {
      const response = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'パスワード変更に失敗しました');
      }

      setSnackbar({ open: true, message: 'パスワードを変更しました', severity: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'パスワード変更に失敗しました';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setChanging(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            マイページ
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>
            ログアウト
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        {/* アカウント情報 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              アカウント情報
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography><strong>メールアドレス:</strong> {user?.email}</Typography>
            <Typography><strong>氏名:</strong> {user?.full_name || '未設定'}</Typography>
          </CardContent>
        </Card>

        {/* 契約プロダクト */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              契約プロダクト
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {user?.products && user.products.length > 0 ? (
              <Grid container spacing={2}>
                {user.products.map((product, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {product.product_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          プラン: {product.plan_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          デバイス: 最大{product.max_devices}台
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={product.is_active ? '有効' : '無効'}
                            color={product.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography color="text.secondary">契約中のプロダクトはありません</Typography>
            )}
          </CardContent>
        </Card>

        {/* パスワード変更 */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              パスワード変更
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type={showCurrentPassword ? 'text' : 'password'}
                  label="現在のパスワード"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  label="新しいパスワード"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  helperText="8文字以上"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  label="新しいパスワード（確認）"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                  helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? '一致しません' : ''}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={changing || !currentPassword || !newPassword || !confirmPassword}
                startIcon={changing ? <CircularProgress size={20} /> : null}
              >
                {changing ? '変更中...' : 'パスワードを変更'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
